// 原系統主體區欄位的 React 版本（四個頁型模板共用）。
//
// 資料來自 src/mocks/legacy-fields.ts（規則層）→ legacy-fields.generated.ts（資料層）
// → scripts/extract-legacy-fields.ts（從 legacy raw DOM 抽）。
//
// 它是 LegacySearchFields.tsx 的超集：後者只涵蓋「查詢區的第一個 form」，
// 本元件涵蓋整個主體區的 input／select／textarea。
//
// ---------------------------------------------------------------------------
// 這個元件存在的理由：功能契約的 fields 維度以 `name` 為比對 key
// ---------------------------------------------------------------------------
// FUNCTIONAL-CONTRACT.md D-2 規定 React 沿用 legacy 的 `name`；
// scripts/compare-contracts.ts 的 `fieldKey()` 就是 `f.name`，而 React observed 抽取時
// 「`if (name === '') continue;`」會把沒有 name 的控制項整個丟掉。
// 舊版 complex-form 頁面用 `FieldControl`（shared.tsx）畫欄位，那條路徑一個 `name`
// 都沒有輸出，所以那些欄位是「畫了但量不到」。本元件輸出真正的 `name`。
//
// ---------------------------------------------------------------------------
// 每個控制項都是受控元件，而且值真的會送出（criteria C5-h）
// ---------------------------------------------------------------------------
// LegacySearchFields 現行版完全沒有 `value`／`onChange`，是裝飾品。本元件相反：
// 每一個帶 name 的控制項都有 `value`（或 `checked`）與 `onChange`，
// 值進 `values[name]`，再由模板送進 `dataSource.list({ filters })`
// 或 `dataSource.create/update(values)`。
//
// **兩類控制項在 React 裡沒有「使用者改值」這個事件**（hidden、以及一個非空選項都沒有的
// select），它們的接線改由「值一定會被送出」保證：初始值 `seedValueFor()`＋
// `toPayload()` 一律帶出 key。判定與理由全部寫在 `src/mocks/legacy-fields.ts` 的檔頭。
//
// ---------------------------------------------------------------------------
// 結構對齊：不加任何有「直屬文字」的 wrapper（沿用 LegacySearchFields 檔頭的推導）
// ---------------------------------------------------------------------------
// 每個節點各自帶 `style={{ gridColumn: '1 / -1' }}` 獨占一列，避免 FormGrid 的
// auto-placement 讓 label 配到別人的控制項。`style` 不在 normalize-dom 的 ATTR_ALLOW 內，
// 對 structure 比對沒有影響。
//
// 值的政策：一律不搬原系統的資料值。初始值是空字串（例外：hidden 常數參數由 seedValueFor
// 帶入；static select 命中 SELECT_DEFAULT_OVERRIDES 的欄位由 initialValues(pageId) 接
// legacy selected——W9 批次 42 裁決①B）；static select 的列舉代碼與
// checkbox／radio 的 value 代碼是「欄位語意」不是資料（見 legacy-fields.ts 檔頭）。

import { type CSSProperties, Fragment, type ReactElement, type ReactNode } from 'react';
import { FIELD_BASE_CLASS } from '@/components/base/shared-classes';
import { type LegacyFieldControl, optionsFor, searchInputAttributesFor } from '@/mocks';
import { optionSetFor } from '@/mocks/legacy-fields';
import { selectPromptFor } from '@/mocks/dynamic-select-prompt';

export interface LegacyContractFieldsProps {
  pageId: string;
  /** 已經過 legacyContractFieldsFor() 去重與 CSRF 排除的欄位。 */
  fields: readonly LegacyFieldControl[];
  values: Readonly<Record<string, string>>;
  onChange: (name: string, next: string) => void;
  disabled?: boolean;
  /** 唯讀（view-only 狀態）。 */
  readOnly?: boolean;
  /**
   * 批次 43（#41）：不畫欄位級 label（複雜查詢群組用：legacy 一個 label 對多個控制項，
   * label 由呼叫端的群組殼負責；formDepartment/formTemplate/account/classDateEnd 群）。
   * radio/checkbox 的 label-wrap 本來就不受此影響（那是控制項文字，不是欄位 label）。
   */
  hideLabels?: boolean;
  /**
   * 批次 44（#42）：特定欄位控制項之後的額外節點（planFormList.jsp:639/:654 的
   * resetDateTimePicker 圓鈕＝日期/填表者組的清除鈕，icon-only 空文字按鈕＝批次 41 前例）。
   * 鍵＝欄位 name；未傳＝零影響。
   */
  afterFieldNodes?: Readonly<Record<string, ReactNode>>;
}

/** 見 LegacySearchFields 檔頭「R2」：每個節點獨占整個 grid row，避免欄位錯位。
 * W11：導出供行為層 extraFields（queryType）的 label/select 同構使用。 */
export const FULL_ROW_STYLE: CSSProperties = { gridColumn: '1 / -1' };

/** W11：契約欄位 label 的 class（與 LegacyContractFields 欄位 label 同款，行為層 extraFields 共用）。 */
export const FIELD_LABEL_CLASS = 'mb-[5px] inline-block font-bold text-ink';

/** 勾選／單選：勾起來時送出的值。原系統的 value 代碼優先，沒有時用瀏覽器預設的 `on`。
 * 【批次 43 教訓】嘗試改為「value='' 保留空字串」破壞 C5-h 凍結慣例（drive() 期望 'on'、
 * formStatus/formDone/selectAll 五頁全紅）→ 回退；value='' 的序列化差（legacy value=""
 * vs react value="on"）歸序列化族 AD（R8 工具批同族）登記，不逐頁硬修。 */
export function checkedValueOf(field: LegacyFieldControl): string {
  return field.value === null || field.value === '' ? 'on' : field.value;
}

function renderControl(
  pageId: string,
  field: LegacyFieldControl,
  value: string,
  onChange: (name: string, next: string) => void,
  disabled: boolean,
  readOnly: boolean,
): ReactNode {
  const common = {
    style: FULL_ROW_STYLE,
    name: field.name,
    required: field.required,
    disabled,
  };

  if (field.hidden) {
    // hidden 欄位：原系統用它承載 refType／action／planId 這類參數。
    //
    // 【R1 的註解寫錯了，這裡更正】R1 版寫「使用者／測試改值後會跟著送進 DataSource」——
    // 那句話是假的：React 的合成事件系統**不對 `input[type=hidden]` 派發 change/input 的
    // onChange**（驗證者用最小探針做過陽性對照：同一個元件下 text 觸發、hidden 不觸發），
    // 所以這個 onChange 永遠不會被呼叫，R1 的 42 個 hidden 欄位全部是空殼。
    //
    // 【R2 的作法】hidden 的「接線」不可能是「使用者改值」，只能是**值一定會被送出**：
    //   - 初始值由 `seedValueFor()` 從 legacy raw DOM 的 `value` 屬性帶入
    //     （只帶常數參數，資料型的識別碼與擷取當下的日期不帶，見 legacy-fields.ts 檔頭）；
    //   - `toPayload()` 對不可驅動的欄位**一律帶出 key**，即使值是空字串。
    // onChange 仍然保留：它是受控元件的必要條件（沒有它 React 會警告），
    // 而且 hidden 欄位日後若改由 route 參數或 session 注入，這條路徑就是注入點。
    return (
      <input
        type="hidden"
        name={field.name}
        required={field.required}
        value={value}
        onChange={(event) => {
          onChange(field.name, event.target.value);
        }}
      />
    );
  }

  if (field.type === 'checkbox' || field.type === 'radio') {
    const checkedValue = checkedValueOf(field);
    return (
      <label style={FULL_ROW_STYLE} className="inline-flex items-center gap-1 text-ink text-sm">
        <input
          type={field.type}
          name={field.name}
          value={checkedValue}
          required={field.required}
          disabled={disabled || readOnly}
          checked={value === checkedValue}
          className="size-4 accent-brand"
          onChange={(event) => {
            onChange(field.name, event.target.checked ? checkedValue : '');
          }}
        />
        {field.label}
      </label>
    );
  }

  if (field.type === 'select' || field.type === 'select-multiple') {
    // 批次 52（§8-A 裁決＋planinst 先例同構）：override 命中偵測——
    // ①override＝[]（凍結真=真空）→ 0 options，連 prompt 都不畫；
    // ②非空 override 且無 prompt（或 prompt 同款選項已在 override 內＝去重）→ 不畫
    //   預設空 option／prompt option（override 選項集即凍結全集；legacy 單選項態對齊）；
    // ③prompt 命中且 override 未含同款 → prompt 照畫（批次 29/35 先例），override 供第二項起。
    const override = optionSetFor(pageId, field.name);
    const options = optionsFor(pageId, field);
    // GNL-002（批次 29，issue #30；W7 批次 42 裁決②案 B 後現況）：dynamic（外鍵）下拉的
    // 寫死首項（全部/請選擇/未設定…）由 selectPromptFor（override 表）提供，首項 prompt
    // option 由本渲染端畫（optionsFor 已不再 prepend）。
    // select 的顯示值對齊：dynamic 且 state 未選（''）時顯示首項 prompt（瀏覽器對無 selected
    // option 預設選首項；select.value 屬性進 structure-diff 比對，不對齊會新增 select value 差）。
    // 【W9 批次 42 裁決①B】原「static select 表單 state 保持 ''（未選語意）——送出值與
    // legacy（首項值）差異記錄結案」的慣例**就此改判**：命中 SELECT_DEFAULT_OVERRIDES
    // （legacy-fields.ts；4 頁 isEnable，初始值由 initialValues(pageId) 接 legacy selected）
    // 的欄位，畫面顯示與查詢送出值都與 legacy 一致；option 的 selected 屬性序列化差
    // 殘 1 筆/頁＝R8 工具批。未命中欄位維持 '' 未選語意不變。
    const prompt =
      field.optionsKind === 'dynamic' && !field.multiple
        ? selectPromptFor(pageId, field.name)
        : undefined;
    // prompt 同款選項已在 override 內＝不另畫 prompt（C2 去重；displayValue 同步不接 prompt）。
    const promptDupInOverride =
      prompt !== undefined &&
      override !== undefined &&
      override.some((o) => o.value === prompt.value && o.text === prompt.text);
    const displayValue =
      field.multiple || prompt === undefined || promptDupInOverride || value !== ''
        ? value
        : prompt.value;
    return (
      <select
        {...common}
        multiple={field.multiple}
        disabled={disabled || readOnly}
        className={FIELD_BASE_CLASS}
        value={field.multiple ? (value === '' ? [] : value.split(',')) : displayValue}
        onChange={(event) => {
          onChange(
            field.name,
            field.multiple
              ? [...event.target.selectedOptions].map((o) => o.value).join(',')
              : event.target.value,
          );
        }}
      >
        {/*
          value="" 的提示項＝未選，**只有 dynamic（外鍵）下拉才加**。
          static 列舉的 option 值集合會被逐值比對（compare-contracts.ts 的 optionsEqual），
          憑空多一個 '' 會讓整個欄位判不相符；dynamic 的 options 只比「存在與 multiple」，
          加提示項不影響任何比對。
        */}
        {field.optionsKind === 'dynamic' && !field.multiple ? (
          override !== undefined && override.length === 0 ? null : // 批次 52 ①：凍結真=真空，0 options
          prompt !== undefined && !promptDupInOverride ? (
            <option value={prompt.value}>{prompt.text}</option>
          ) : override !== undefined ? null : ( // 批次 52 ②：非空 override 且不畫 prompt
            <option value="" />
          )
        ) : null}
        {options.map((option) => (
          <option key={`${option.value} ${option.text}`} value={option.value}>
            {option.text}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'textarea') {
    return (
      <textarea
        {...common}
        readOnly={readOnly}
        className={FIELD_BASE_CLASS}
        value={value}
        onChange={(event) => {
          onChange(field.name, event.target.value);
        }}
      />
    );
  }

  if (field.type === 'file') {
    // 檔案輸入不能是受控元件（value 只能由使用者設定），所以不給 value；
    // 選檔之後把檔名寫進 state，一樣會跟著送出。
    return (
      <input
        {...common}
        type="file"
        className={FIELD_BASE_CLASS}
        onChange={(event) => {
          onChange(field.name, event.target.files?.[0]?.name ?? event.target.value);
        }}
      />
    );
  }

  // W8（批次 42，issue #40）：查詢 form 契約已有的 maxLength／placeholder，渲染時帶上。
  // 批次 43：補 readOnly（classDateStart/End 的 datepicker 欄位，queryPage.jsp:136/145
  // readonly 屬性；legacy-search-forms.generated 有 readOnly:true）。
  const inputAttrs = field.type === 'text' ? searchInputAttributesFor(pageId, field.name) : undefined;
  return (
    <input
      {...common}
      type={field.type}
      readOnly={readOnly || inputAttrs?.readOnly === true}
      className={FIELD_BASE_CLASS}
      value={value}
      {...(inputAttrs?.maxLength === undefined ? {} : { maxLength: inputAttrs.maxLength })}
      {...(inputAttrs?.placeholder === undefined ? {} : { placeholder: inputAttrs.placeholder })}
      onChange={(event) => {
        onChange(field.name, event.target.value);
      }}
    />
  );
}

export function LegacyContractFields({
  pageId,
  fields,
  values,
  onChange,
  disabled = false,
  readOnly = false,
  hideLabels = false,
  afterFieldNodes,
}: LegacyContractFieldsProps): ReactElement {
  return (
    <>
      {fields.map((field) => (
        // 同一頁的 name 已經在 legacyContractFieldsFor() 去重過，可以當穩定 key；
        // 批次 43：radio 變體群（formDone×3）同 name 異 value，key 加 value 区隔。
        <Fragment key={field.value ? `${field.name}¦${field.value}` : field.name}>
          {field.hidden ||
          field.label === '' ||
          field.type === 'checkbox' ||
          field.type === 'radio' ||
          hideLabels ? null : (
            // biome-ignore lint/a11y/noLabelWithoutControl: 逐字複製 legacy 的純文字 label（同 LegacySearchFields）；htmlFor/id 在 normalize-dom 的屬性 allowlist 之外，加了不影響 structure 比對。
            <label style={FULL_ROW_STYLE} className={FIELD_LABEL_CLASS}>
              {field.label}
            </label>
          )}
          {renderControl(pageId, field, values[field.name] ?? '', onChange, disabled, readOnly)}
          {afterFieldNodes === undefined ? null : afterFieldNodes[field.name] ?? null}
        </Fragment>
      ))}
    </>
  );
}
